import { Button, Modal, Textarea, RadioGroup, Radio } from "rizzui";
import { DatePicker } from "../ui/datepicker";
import { useCallback, useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  UserSuspendUpdateDTO,
  UserSuspendUpdateDTOSchema,
} from "@/utilities/validators/suspension";
import {
  EUserSuspendMode,
  ICreateUserSuspend,
  IUserSuspend,
  IUserSuspendUpdate,
} from "@/utilities/types/suspension";
import { Portal } from "@headlessui/react";
import SystemSettingRepository from "@/utilities/repositories/SystemSetting";
import toast from "react-hot-toast";
import { IUserData } from "@/utilities/types/Users";

type SuspendModalProps = {
  open: boolean;
  userSuspend: IUserSuspend | null;
  onClose: () => void;
  user: IUserData | null;
  refresh: () => Promise<void>;
};

const SuspendModal = ({
  open,
  userSuspend,
  onClose,
  user,
  refresh,
}: SuspendModalProps) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<UserSuspendUpdateDTO>({
    resolver: zodResolver(UserSuspendUpdateDTOSchema),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const mode = watch("mode");

  useEffect(() => {
    reset({
      mode: userSuspend?.mode || EUserSuspendMode.IMMEDIATE,
      reason: userSuspend?.reason || "",
      date: userSuspend?.date ? new Date(userSuspend.date) : undefined,
    });
  }, [userSuspend?.id, reset, user?.id]);

  const createUserSuspend = useCallback(async (data: ICreateUserSuspend) => {
    try {
      await SystemSettingRepository.createUserSuspend(data);
      toast.success("User suspended successfully");
      await refresh();
    } catch (error) {
      toast.error("Error creating user suspend");
      console.error("Error creating user suspend:", error);
    }
  }, []);

  const updateUserSuspend = useCallback(
    async (id: string, data: IUserSuspendUpdate) => {
      try {
        await SystemSettingRepository.updateUserSuspend(id, data);
        toast.success("User suspended successfully");
        await refresh();
      } catch (error) {
        toast.error("Error creating user suspend");
        console.error("Error creating user suspend:", error);
      }
    },
    []
  );

  const deleteUserSuspend = useCallback(async (id: string) => {
    try {
      setIsSubmitting(true);
      await SystemSettingRepository.deleteUserSuspend(id);
      toast.success("User unsuspended successfully");
      await refresh();
    } catch (error) {
      toast.error("Error deleting user suspend");
      console.error("Error deleting user suspend:", error);
    } finally {
      setIsSubmitting(false);
      reset({
        mode: EUserSuspendMode.IMMEDIATE,
        reason: "",
        date: undefined,
      });
      onClose();
    }
  }, []);

  const onSubmit: SubmitHandler<UserSuspendUpdateDTO> = useCallback(
    (data) => {
      setIsSubmitting(true);
      if (userSuspend?.id) {
        const updateUserSuspendData: IUserSuspendUpdate = {
          mode: data.mode,
          reason: data.reason,
          date:
            data.mode === EUserSuspendMode.SCHEDULE && data.date
              ? new Date(data.date).toISOString()
              : undefined,
        };
        updateUserSuspend(userSuspend.id, updateUserSuspendData);
      } else {
        const createUserSuspendData: ICreateUserSuspend = {
          userId: user?.id || "",
          mode: data.mode,
          reason: data.reason,
          date:
            data.mode === EUserSuspendMode.SCHEDULE && data.date
              ? new Date(data.date).toISOString()
              : undefined,
        };
        createUserSuspend(createUserSuspendData);
      }
      setIsSubmitting(false);
      onClose();
    },
    [userSuspend, updateUserSuspend, createUserSuspend, onClose, refresh]
  );

  return (
    <Modal isOpen={open} onClose={onClose}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 p-6 min-h-fit"
      >
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">Suspend Website</div>
          <button type="button" onClick={onClose}>
            <IoMdClose size={24} />
          </button>
        </div>

        <div>
          <label className="block mb-1 font-medium">Mode</label>
          <Controller
            name="mode"
            control={control}
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                setValue={field.onChange}
                className="flex space-x-4"
              >
                <Radio label="Immediate" value={EUserSuspendMode.IMMEDIATE} />
                <Radio label="Schedule" value={EUserSuspendMode.SCHEDULE} />
              </RadioGroup>
            )}
          />
        </div>

        {mode === EUserSuspendMode.SCHEDULE && (
          <div className="w-full">
            <label className="block mb-1 font-medium">Suspension Date</label>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  dateFormat="dd MMM, yyyy"
                  selected={field.value ? new Date(field.value) : null}
                  onChange={(date: Date) => field.onChange(date)}
                  inputProps={{
                    error: errors.date?.message,
                  }}
                  popperContainer={({ children }) => (
                    <Portal>{children}</Portal>
                  )}
                  className="z-50"
                />
              )}
            />
          </div>
        )}

        <div>
          <label className="block mb-1 font-medium">Reason</label>
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <Textarea {...field} error={errors.reason?.message} />
            )}
          />
        </div>
        <div className="flex justify-between">
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>

          <div className="flex justify-end gap-2">
            {!!userSuspend && (
              <Button
                type="button"
                variant="outline"
                onClick={() => deleteUserSuspend(userSuspend?.id || "")}
                disabled={isSubmitting}
              >
                Unsuspend
              </Button>
            )}
            <Button type="submit" color="danger" disabled={isSubmitting}>
              Confirm Suspend
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default SuspendModal;
